import { Chip, Grid, makeStyles } from "@material-ui/core";
import { theme } from "config/theme";
import useText from "constants/text";
import { checkReccurenceIntervalDate, NewRequestQuoteContext } from "contexts/RequestQuoteContext";
import { EProductType } from "generated/graphql";
import React from "react";
import Autocomplete from "ui/Autocomplete";
import { TextField } from "ui/TextField";
import { formatDate, isFieldWithValue } from "../../shared/utils";
import Navigation from "./Navigation";

const useStyles = makeStyles(() => ({
  root: {
    height: "63vh",
    overflowY: "scroll",
    overflowX: "hidden",
    display: "flex",
    marginLeft: "auto",
    width: "33rem",
    marginRight: "auto",
    [theme.breakpoints.down("xs")]: {
      display: "flex",
      marginLeft: "0px",
      marginRight: "0px",
      width: "unset",
      marginTop: -41,
    },
  },
  gridContainer: {
    padding: "0rem 0rem 0rem 0rem",
    display: "block",
    margin: "auto",
    [theme.breakpoints.down("xs")]: {
      padding: "0rem 0rem 0rem 0rem",
      display: "block",
    },
  },
}));
export interface IProps {
  onNext: () => void;
  onCancel?: () => void;
  isRecapEditing?: boolean;
  onBack?: () => void;
  isTour?: boolean;
}

export default function ReccurenceTour({ onNext, onCancel, onBack }: IProps) {
  const { requestQuoteTour, setRequestQuoteTour, setStep } = React.useContext(NewRequestQuoteContext);
  const { text } = useText();
  const styles = useStyles();
  const productTypeChip = {
    backgroundColor: "#399ebf",
    color: "white",
  };

  const productsTypeOptions = [
    {
      value: EProductType.Couriers,
      description: text.requestQuote.product_type_courrier,
    },
    {
      value: EProductType.Fragile,
      description: text.requestQuote.product_type_fragile,
    },
    {
      value: EProductType.Food,
      description: text.requestQuote.product_type_food,
    },
    {
      value: EProductType.FreshProducts,
      description: text.requestQuote.product_type_fresh_products,
    },
    {
      value: EProductType.Other,
      description: text.requestQuote.product_type_other,
    },
  ];
  const onNextClick = () => {
    setStep(2);
    onNext();
  };
  const [filterOptions, setFilterOptions] = React.useState(productsTypeOptions);

  React.useEffect(() => {
    if (requestQuoteTour?.productType) {
      setFilterOptions(
        productsTypeOptions.filter(
          (x) => requestQuoteTour?.productType.findIndex((e: any) => x.value === e?.value || x.value === e) === -1
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestQuoteTour?.productType]);

  React.useEffect(() => {
    if (!isFieldWithValue(requestQuoteTour?.startDate)) {
      setRequestQuoteTour({ ...requestQuoteTour, startDate: formatDate(0), endDate: formatDate(0) });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const onChangeDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRequestQuoteTour({
      ...requestQuoteTour,
      [event.target.name]: event.target.value,
    });
  };

  const handleAdditional = (event: any) => {
    setRequestQuoteTour({ ...requestQuoteTour, additionnalInfos: event.target.value });
  };

  const handleChange = (newArray: any) => {
    setRequestQuoteTour({
      ...requestQuoteTour,
      productType: newArray.map((productType: any) => productType?.value || productType),
    });
  };

  const getOptionValueDescription = (option: any) =>
    productsTypeOptions?.find((item) => option?.value === item.value || option === item.value)?.description || "";

  return (
    <NewRequestQuoteContext.Consumer>
      {({ requestQuoteTour, fieldError }) => (
        <>
          <div className={styles.root}>
            <Grid container direction="column" spacing={4} className={styles.gridContainer}>
              <Grid item xs={12} md={12}>
                <Autocomplete
                  multiple
                  fullWidth
                  id="fixed-tags-demo"
                  label={text.requestQuote.select_product_type}
                  value={requestQuoteTour?.productType}
                  onChange={(event: any, newValue: any) => {
                    handleChange(newValue);
                  }}
                  options={filterOptions}
                  getOptionLabel={(option: any) => option.description}
                  renderTags={(tagValue: any, getTagProps: any) =>
                    tagValue.map((option: any, index: any) => (
                      <Chip
                        label={getOptionValueDescription(option)}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...getTagProps({ index })}
                        style={productTypeChip}
                      />
                    ))
                  }
                  error={fieldError && !requestQuoteTour?.productType?.length}
                  helperText={
                    fieldError && !requestQuoteTour?.productType?.length ? text.all.this_field_is_required : ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  type="date"
                  fullWidth
                  label={text.all.start_date}
                  name="startDate"
                  id="date"
                  value={formatDate(0, requestQuoteTour?.startDate) || formatDate(1)}
                  onChange={onChangeDate}
                  inputProps={{ min: formatDate(0) }}
                  error={fieldError && !isFieldWithValue(requestQuoteTour?.startDate)}
                  helperText={
                    fieldError
                      ? isFieldWithValue(requestQuoteTour?.startDate)
                        ? text.all.this_field_is_required
                        : !checkReccurenceIntervalDate(requestQuoteTour)
                        ? text.all.intervall_date_error
                        : ""
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  type="date"
                  fullWidth
                  label={text.all.end_date}
                  name="endDate"
                  id="date"
                  value={formatDate(0, requestQuoteTour?.endDate) || formatDate(1)}
                  onChange={onChangeDate}
                  inputProps={{ min: formatDate(0) }}
                  error={fieldError && !isFieldWithValue(requestQuoteTour?.endDate)}
                  helperText={
                    fieldError
                      ? isFieldWithValue(requestQuoteTour?.endDate)
                        ? text.all.this_field_is_required
                        : !checkReccurenceIntervalDate(requestQuoteTour)
                        ? text.all.intervall_date_error
                        : ""
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  onChange={handleAdditional}
                  multiline
                  rows={2}
                  value={requestQuoteTour.additionnalInfos}
                  placeholder={text.requestQuote.additionnal_details}
                  fullWidth
                />
              </Grid>
            </Grid>
          </div>
          <Navigation
            cancelTitle={text.all.cancel}
            nextTitle={text.all.next}
            previousTitle={text.all.previous}
            onNext={onNextClick}
            onPrevious={onBack}
            onCancel={onCancel}
            disableNext={!checkReccurenceIntervalDate(requestQuoteTour) || !requestQuoteTour?.productType?.length }
          />
        </>
      )}
    </NewRequestQuoteContext.Consumer>
  );
}
